// Casora Design Token System — Single source of truth for all visual values

// Admin Console material study. Explicitly scoped; existing customer themes are unchanged.
export const founderConsoleThemes = {
  dark: {
    '--fw-bg': '#111416', '--fw-surface': '#1b1f22', '--fw-raised': '#252a2e',
    '--fw-text': '#f2f4f3', '--fw-muted': '#a8b0b3', '--fw-border': '#363d41',
    '--fw-field': '#15191c', '--fw-control-edge': '#68757c',
    '--fw-control-surface': '#282e32', '--fw-control-hover': '#353d42',
    '--fw-control-pressed': '#20262a', '--fw-control-highlight': 'rgba(255,255,255,.12)',
    '--fw-selected': '#e9eeec', '--fw-selected-text': '#19221f',
    '--fw-panel-shadow': '0 1px 0 rgba(255,255,255,.025)',
  },
  light: {
    '--fw-bg': '#f3f4f1', '--fw-surface': '#ffffff', '--fw-raised': '#e9ece7',
    '--fw-text': '#202824', '--fw-muted': '#56605a', '--fw-border': '#cbd2ca',
    '--fw-field': '#fafbf8', '--fw-control-edge': '#7a867d',
    '--fw-control-surface': '#f5f7f2', '--fw-control-hover': '#ffffff',
    '--fw-control-pressed': '#e4e9e1', '--fw-control-highlight': 'rgba(255,255,255,.95)',
    '--fw-selected': '#25372e', '--fw-selected-text': '#ffffff',
    '--fw-panel-shadow': '0 2px 4px rgba(29,43,34,.025), 0 12px 32px rgba(29,43,34,.035)',
  },
};
export const utilityMaterial = { highlight: '#ffffff', shade: '#000000' };

export const colors = {
  brand: {
    green: '#22c55e',
    blue: '#3b82f6',
    amber: '#eab308',
    red: '#ef4444',
    purple: '#8b5cf6',
    orange: '#f59e0b',
  },
  // S107 / S42-fu — semantic token for the "Common Area" unit-type marker
  // used across fleet surfaces (FleetDashboard meter banner, UnitCard,
  // FleetPropertyCard, FleetSensors, AdvancedView, AccountSection).
  // The hex (#a855f7, Tailwind purple-500) is intentionally distinct from
  // colors.brand.purple (#8b5cf6, Tailwind violet-500) — the former marks
  // shared/landlord-managed units, the latter is the brand purple used for
  // pricing/plan accents.
  commonArea: '#a855f7',
  utility: {
    electricity: '#22c55e',
    water: '#3b82f6',
    gas: '#eab308',
  },
  tou: {
    onPeak: '#ef4444',
    midPeak: '#f59e0b',
    offPeak: '#22c55e',
    // F109: the Ultra-Low Overnight (ULO plan) window — cheapest tier, cooler
    // than off-peak green. Was an off-palette '#6366f1' literal repeated at 3
    // Analytics sites behind a `TC.ultraLow || …` fallback that never resolved
    // (TC.ultraLow didn't exist); tokenized here, value preserved (zero
    // visual change to the latent ULO path).
    ultraLow: '#6366f1',
  },
  dark: {
    bg: '#0a0b10',
    bgGradient: 'linear-gradient(160deg,#0a0b10 0%,#111318 50%,#0a0b10 100%)',
    surface: 'rgba(255,255,255,0.035)',
    surfaceBorder: 'rgba(255,255,255,0.06)',
    surfaceHover: 'rgba(255,255,255,0.06)',
    text: '#f1f5f9',
    textSecondary: 'rgba(255,255,255,0.6)',
    // F020 (P2.5c): raised 0.3→0.5. At 0.3 the token computed ≈2.4-2.8:1 in
    // both themes yet is load-bearing app-wide (address, dock labels, chart
    // axes, rates, rule descriptions). 0.5 clears AA on the dark canvas while
    // staying visibly quieter than textSecondary (0.6). Linear's tertiary
    // holds ≈4.6:1 — quiet via size/weight, never near-zero opacity.
    textTertiary: 'rgba(255,255,255,0.5)',
    glass: 'rgba(255,255,255,0.05)',
    glassBorder: 'rgba(255,255,255,0.12)',
  },
  light: {
    bg: '#f8fafc',
    bgGradient: 'linear-gradient(160deg,#f8fafc 0%,#f1f5f9 50%,#f8fafc 100%)',
    surface: 'rgba(255,255,255,0.75)',
    surfaceBorder: 'rgba(0,0,0,0.06)',
    surfaceHover: 'rgba(255,255,255,0.9)',
    text: '#0f172a',
    textSecondary: '#475569',
    // F020 (P2.5c): #94a3b8 (slate-400) ran ≈2.6:1 on the light canvas —
    // sub-AA. #64748b (slate-500) clears AA while reading as quiet chrome.
    textTertiary: '#64748b',
    glass: 'rgba(255,255,255,0.6)',
    glassBorder: 'rgba(0,0,0,0.06)',
  },
};

// Canonical Casora brand-mark order. Use this for every unlabeled tri-dot
// motif; labelled utility data should continue to use colors.utility so its
// values stay attached to electricity, water, and gas.
export const BRAND_DOT_COLORS = Object.freeze([
  colors.brand.green,
  colors.brand.amber,
  colors.brand.blue,
]);

export const alpha = (hex, opacity) => {
  // Support both 3-digit (#fff) and 6-digit (#ffffff) hex
  let h = hex.startsWith('#') ? hex.slice(1) : hex;
  if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity})`;
};

// S52-U1: numeric display style. Apply to any element whose primary content
// is numeric (dashboard tiles, fleet cards, prices, counters, charts) so live
// values don't jiggle the layout 1-3px per update. Do NOT apply globally to
// body — proportional figures read better in flowing prose.
export const numericStyles = { fontVariantNumeric: 'tabular-nums' };

// S55-T3 (Roadmap Phase 1): semantic status colors. Returns matched bg / fg /
// border alphas for the 4 status levels so a "warning pill" doesn't have
// to hand-roll its own alpha math every time. Levels map to brand hues:
//   success  → brand.green
//   warning  → brand.amber
//   danger   → brand.red
//   info     → brand.blue
//
// Convention:
//   const s = statusStyle('success');
//   <div style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}>
//
// Returned shape:
//   bg     — subtle tinted background (alpha 0.10) — pill backgrounds, banner fills
//   fg     — full-strength foreground — text + icons inside the pill
//   border — muted border (alpha 0.25) — pill / banner outlines
//   hex    — raw hex for ad-hoc shadow / glow / gradient construction
const STATUS_HUE = {
  success: '#22c55e', // colors.brand.green
  warning: '#eab308', // colors.brand.amber
  danger:  '#ef4444', // colors.brand.red
  info:    '#3b82f6', // colors.brand.blue
};
// F004 (P2.5c): light-theme foreground overrides. The bright brand hues sit
// at 1.7-3.8:1 as text on the light canvas — sub-AA everywhere. Stripe/Linear
// darken semantic hues for text (green-700 on green-50) as table stakes, so
// on light we swap fg to the -700/-800 shade while keeping bg/border alphas
// from the bright hex (a faint bright tint under darkened ink — the canonical
// green-50/green-700 pill). Dark theme is unchanged: the bright hue already
// clears AA on the near-black canvas.
const STATUS_FG_LIGHT = {
  success: '#15803d', // green-700
  warning: '#a16207', // amber-700
  danger:  '#b91c1c', // red-700
  info:    '#1d4ed8', // blue-700
};
export const statusStyle = (level, theme = 'dark') => {
  const hex = STATUS_HUE[level] || STATUS_HUE.info;
  return {
    hex,
    bg:     alpha(hex, 0.10),
    fg:     theme === 'light' ? (STATUS_FG_LIGHT[level] || STATUS_FG_LIGHT.info) : hex,
    border: alpha(hex, 0.25),
  };
};

// F004 (P2.5c): utility hues darkened for light-theme text use. Same defect
// class as statusStyle — the electricity/water/gas brand hexes render sub-AA
// as ink on the light canvas (green ≈2.1:1, gas/amber ≈1.7:1). Use this for
// any utility color consumed as TEXT; keep colors.utility.* for dots, icon
// chips, strokes, and gradient fills (hue-on-response is fine at ≥3:1).
//   const c = utilityFg('gas', theme);  // darkened on light, bright on dark
const UTILITY_FG_LIGHT = {
  electricity: '#15803d', // green-700
  water:       '#1d4ed8', // blue-700
  gas:         '#a16207', // amber-700
};
export const utilityFg = (utility, theme = 'dark') =>
  theme === 'light'
    ? (UTILITY_FG_LIGHT[utility] || colors.utility[utility])
    : (colors.utility[utility] || '#3b82f6');

// S53-T2 (Roadmap Phase 1): canonical typography scale. Use these tokens
// over raw font.size values so type rhythm stays consistent. Each role
// has size + weight + lineHeight + letterSpacing baked in — letterSpacing
// gets tighter at larger sizes (Linear/Stripe Atlas convention) so display
// type doesn't feel airy and small-caps labels get tracking for legibility.
//
// Convention:
//   - Inline-style: `style={{ fontSize: typography.micro.size, fontWeight: typography.micro.weight, letterSpacing: typography.micro.letterSpacing }}`
//   - Or spread the whole role into a helper: `style={{ ...textStyle('micro'), color: ... }}`
//     where `textStyle(role)` is your component's local helper.
//
// Role       Px  Wt   LH    Track    Use
// display    32  800  1.1   -0.02em  Hero numbers, page-level h1
// title      22  800  1.2   -0.01em  Section headers, bill totals
// subtitle   16  700  1.3   0        Card titles, tab labels
// body       14  500  1.5   0        Body text, descriptions
// caption    12  600  1.4   0        Inline labels, badges
// micro      11  700  1.2   0.1em    Small-caps section eyebrows, timestamps
export const typography = {
  display:  { size: 32, weight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' },
  title:    { size: 22, weight: 800, lineHeight: 1.2, letterSpacing: '-0.01em' },
  subtitle: { size: 16, weight: 700, lineHeight: 1.3, letterSpacing: '0' },
  body:     { size: 14, weight: 500, lineHeight: 1.5, letterSpacing: '0' },
  caption:  { size: 12, weight: 600, lineHeight: 1.4, letterSpacing: '0' },
  micro:    { size: 11, weight: 700, lineHeight: 1.2, letterSpacing: '0.1em' },
};

export const font = {
  family: {
    sans: "'Plus Jakarta Sans','Segoe UI',system-ui,sans-serif",
    mono: "'JetBrains Mono','Courier New',monospace",
  },
  size: {
    xs: 11,
    sm: 13,
    base: 14,
    md: 15,
    lg: 18,
    xl: 22,
    '2xl': 28,
    '3xl': 36,
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  lineHeight: {
    tight: 1.1,
    snug: 1.25,
    normal: 1.5,
    relaxed: 1.625,
  },
  letterSpacing: {
    tight: '-0.5px',
    normal: '0px',
    wide: '0.5px',
    wider: '1px',
    widest: '1.5px',
  },
};

// S53-T1 (Roadmap Phase 1): canonical spacing scale on a 4px base. Use
// these tokens instead of hardcoded margin/padding values in new code so
// the app develops a consistent rhythm. Scale is intentionally tight —
// the gap between 16 (4) and 24 (5) forces premium spacing decisions
// (Linear/Stripe Atlas/Vercel never use 18px or 20px paddings).
//
// Convention:
//   - Inline-style: `padding: \`${spacing[3]}px ${spacing[4]}px\``
//   - Compact value access: `marginBottom: spacing[5]` (number, ready for px)
//   - Don't introduce off-scale values (18, 20, 28) — round to nearest
//     on-scale token; if the visual really needs an off-scale value,
//     leave a comment explaining why.
//
// Token  Px   Common use
// 0      0    flush
// 1      4    inline gap (icon→text), tight stack
// 2      8    chip padding, small gap
// 3      12   form-row gap, medium padding
// 4      16   section padding, card body gap
// 5      24   card outer padding, section break
// 6      32   block padding, card-to-card gap
// 7      48   page-section break (compact)
// 8      64   page-section break (spacious)
// 9      96   hero-to-content break, viewport-edge negative space
export const spacing = {
  0: 0, 1: 4, 2: 8, 3: 12, 4: 16, 5: 24, 6: 32, 7: 48, 8: 64, 9: 96,
};

// S55-T4 (Roadmap Phase 1): canonical radius scale. Updated in place from
// the prior {sm:8, md:12, lg:16, 2xl:24} scale to align with the implicit
// pattern emerging from S53-S1 + S55-S3 (landing CTAs all = 6, cards
// mostly 14-16). The new scale is tighter; existing callers pick up the
// -2px shift automatically via their existing `radius.*` imports.
//
// Convention:
//   sm   6     small inputs, chips, compact buttons (matches landing CTAs)
//   md   10    standard buttons, form fields, badges
//   lg   14    cards, callouts, ticker pills
//   xl   20    modals, hero/feature surfaces
//   2xl  28    large showcase cards, glass panels
//   full 9999  pills, avatar circles
//
// Off-scale values (4, 8, 12, 16, 20, 24) need a justifying comment in new code.
// AUDIT — borderRadius sweep CLOSED across S106 R1 + R2:
//   S106 R1: 16/20 sweep — 14 sites across 8 files
//   S106 R2: 8 sweep   — 22 sites across 13 files → radius.sm
//            24 sweep   — 4 sites across 4 files → radius.{2xl|xl|full}
//            12 sweep   — 79 sites across 27 files → radius.md (mostly) /
//                         radius.lg (cards) / radius.full (paired pill geometry)
// Total: 105 sites / 35 files migrated to canonical radius tokens.
// Grep across src/ for off-scale 8/12/16/20/24 is now ZERO outside this comment.
// borderRadius:4 stays off-scale (sub-scale fine-detail use only — rare).
export const radius = {
  sm: 6,
  md: 10,
  lg: 14,
  xl: 20,
  '2xl': 28,
  full: 9999,
};

// S58-T5 (Roadmap Phase 1): shadow scale convention. Codifies when to reach
// for each elevation level and documents utility-tinted glow shorthands so
// status indicators and live-reading surfaces don't hand-roll their own glow
// math every time.
//
// Convention:
//   sm   — resting surfaces (chips, cards at rest). Very subtle — mostly
//          invisible on dark backgrounds, adds definition in light mode.
//   md   — hover lift: use when a card or button is hovered. Pair with
//          `transition: 'box-shadow 0.2s ease'` in the element's style.
//   lg   — overlay shadow: modals, drawers, tooltips, floating panels.
//   xl   — showcase shadow: hero preview panels, featured cards.
//   glow — ambient colored halo for live indicators / active tabs:
//          `shadow.glow(colors.brand.green)` → `0 0 20px rgba(34,197,94,0.3)`
//
// Utility-tinted shorthands (electricity/water/gas icon glows, status dots):
//   shadow.glowElectricity / .glowWater / .glowGas
//
// Stacking with ring on elevated dark surfaces:
//   `${shadow.lg}, 0 0 0 0.5px rgba(255,255,255,0.04)`
export const shadow = {
  sm: '0 1px 4px rgba(0,0,0,0.12)',
  md: '0 4px 12px rgba(0,0,0,0.15)',
  lg: '0 8px 24px rgba(0,0,0,0.2)',
  xl: '0 12px 40px rgba(0,0,0,0.3)',
  glow: (color) => `0 0 20px ${alpha(color, 0.3)}`,
  glowElectricity: `0 0 20px ${alpha(colors.utility.electricity, 0.3)}`,
  glowWater:       `0 0 20px ${alpha(colors.utility.water, 0.3)}`,
  glowGas:         `0 0 20px ${alpha(colors.utility.gas, 0.3)}`,
};

export const blur = {
  sm: 'blur(8px)',
  md: 'blur(12px)',
  lg: 'blur(20px)',
  xl: 'blur(40px)',
};

// S121-DESIGN-P1 (Casora-Design-Overhaul-Direction.md §3): "Liquid
// Instrument" glass material system. Three semantic tiers, theme-keyed:
//
//   panel — Tier 1, resting cards / insight rows / utility cards
//   float — Tier 2, hero cards / nav dock / sticky chrome / popovers
//   well  — Tier 3, recessed content areas (chart bg, inputs). NO blur —
//           wells are cheap and can appear many times per viewport.
//
// Usage rules (doc §3):
//   - Tier assignment is semantic, never ad-hoc: resting = panel,
//     elevated/hero/chrome = float, recessed = well.
//   - Performance budget: max 6 blurred layers (panel+float) per viewport.
//     Never nest one blurred surface inside another — a card inside a
//     blurred parent uses `well` or a solid fill.
//   - Spread the whole tier into an inline style: `style={{ ...glass.dark.panel }}`
//     (each tier carries background/border/backdropFilter/WebkitBackdropFilter/
//     boxShadow, all React-style-ready).
//   - Interactive glass hover/press language: border brightens one step +
//     shadow.md + translateY(-1px); press scale(0.99). Via useMotionConfig.
//
// Backdrop-filter fallback (doc §3, "add once, globally, in Phase 1"):
// when the engine lacks backdrop-filter support (the JS equivalent of
// `@supports not (backdrop-filter: blur(1px))` — feature-detected once at
// module load because glass is consumed via inline styles, not stylesheets),
// panel/float swap their translucent gradient for the existing solid
// themes[theme].card values (sourced from colors.*.surface here to avoid a
// circular import with constants/theme.js) and drop the blur. Wells never
// blur, so they pass through unchanged.
const HAS_BACKDROP_BLUR =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' &&
  (CSS.supports('backdrop-filter', 'blur(1px)') ||
    CSS.supports('-webkit-backdrop-filter', 'blur(1px)'));

const glassTier = ({ background, border, blur: blurValue, boxShadow, fallbackBackground }) => (
  HAS_BACKDROP_BLUR
    ? {
        background,
        border,
        backdropFilter: blurValue,
        WebkitBackdropFilter: blurValue,
        boxShadow,
      }
    : { background: fallbackBackground, border, boxShadow }
);

export const glass = {
  dark: {
    panel: glassTier({
      background: 'linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.02))',
      border: '1px solid rgba(255,255,255,0.08)',
      blur: 'blur(16px) saturate(140%)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)', // specular top edge
      fallbackBackground: colors.dark.surface,
    }),
    float: glassTier({
      background: 'linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
      border: '1px solid rgba(255,255,255,0.10)',
      blur: 'blur(24px) saturate(160%)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.09), 0 8px 24px rgba(0,0,0,0.2)',
      fallbackBackground: colors.dark.surfaceHover,
    }),
    well: {
      background: 'rgba(0,0,0,0.25)',
      border: '1px solid rgba(255,255,255,0.04)',
      boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.3)',
    },
  },
  light: {
    panel: glassTier({
      background: 'linear-gradient(180deg, rgba(255,255,255,0.82), rgba(255,255,255,0.65))',
      border: '1px solid rgba(15,23,42,0.06)',
      blur: 'blur(16px) saturate(150%)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 4px rgba(15,23,42,0.04)',
      fallbackBackground: colors.light.surface,
    }),
    float: glassTier({
      background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.72))',
      border: '1px solid rgba(15,23,42,0.08)',
      blur: 'blur(24px) saturate(170%)',
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,1), 0 8px 24px rgba(15,23,42,0.08)',
      fallbackBackground: colors.light.surfaceHover,
    }),
    well: {
      background: 'rgba(15,23,42,0.035)',
      border: '1px solid rgba(15,23,42,0.05)',
      boxShadow: 'inset 0 1px 2px rgba(15,23,42,0.05)',
    },
  },
};

// S121-DESIGN-P1 (doc §3, canvas rule): the ONE fixed ambient radial glow
// behind the glass — "this is what makes glass read as glass; there must
// be something soft behind it". Rendered by app chrome as a fixed,
// pointer-events-none, full-viewport layer UNDER the content, on top of
// the existing colors.*.bgGradient canvas. ~800px, top-center. One per
// app shell — never per-surface.
export const ambientGlow = {
  dark: `radial-gradient(circle 800px at 50% -160px, ${alpha(colors.brand.green, 0.04)} 0%, transparent 70%)`,
  light: `radial-gradient(circle 800px at 50% -160px, ${alpha(colors.brand.blue, 0.03)} 0%, transparent 70%)`,
};

// S117-T6 (Roadmap Phase 1): canonical motion scale. Codifies the duration
// + easing tokens that `useMotionConfig` (src/hooks/useMotionConfig.js) and
// CSS keyframe gates in src/index.css were already reaching for as raw
// numbers. Single source of truth lives here so the JS-driven Framer
// transitions and the CSS-driven `*:focus-visible` / `motion-safe` rules
// can't drift apart.
//
// Convention:
//   - JS (Framer): import `useMotionConfig` from `src/hooks/useMotionConfig`,
//     it consumes from this scale + handles `prefers-reduced-motion` collapse.
//   - JS (raw transition value): `transition: \`opacity ${motion.duration.base}ms ${motion.easing.standard}\``
//   - CSS: keep mirrored constants in src/index.css; if you change a value
//     here, sync the CSS var.
//
// Duration scale (ms):
//   instant  0    no motion (matches reduced-motion fallback)
//   fast     150  micro-interactions (hover lift, focus ring fade)
//   base     250  standard transitions (panel open, tab switch)
//   slow     400  emphasis transitions (page change, hero reveal)
//   slower   600  large staged sequences (onboarding intro, score reveal)
//
// Easing tokens (cubic-bezier, Material/Linear convention):
//   standard    [0.4, 0, 0.2, 1]   — default ease-in-out, balanced
//   decelerate  [0, 0, 0.2, 1]     — entering elements, fast start
//   accelerate  [0.4, 0, 1, 1]     — exiting elements, slow start
//   spring      handled via motion.spring.* below (Framer-specific)
export const motion = {
  duration: {
    instant: 0,
    fast: 150,
    base: 250,
    slow: 400,
    slower: 600,
  },
  easing: {
    standard:   [0.4, 0, 0.2, 1],
    decelerate: [0, 0, 0.2, 1],
    accelerate: [0.4, 0, 1, 1],
  },
  spring: {
    snappy: { type: 'spring', stiffness: 400, damping: 30 },
    gentle: { type: 'spring', stiffness: 150, damping: 20 },
    default: { type: 'spring', stiffness: 200, damping: 20 },
  },
};

// `prefers-reduced-motion` helper for non-React contexts (utils, plain JS
// modules) that can't use the `useMotionConfig` hook. Returns true when
// the user has the OS-level reduced-motion preference set, false otherwise.
// In SSR / non-browser contexts returns false.
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// F120 (Casora-Design-Review-2026-07 §3): pointer-aware copy helpers so the
// app never says "Tap" under a mouse cursor (or "Click" on a phone). Mirrors
// prefersReducedMotion — plain-JS + matchMedia, SSR-safe, cheap to call at
// render (pointer capability effectively never changes at runtime). Consumed
// by the tour (DemoWalkthrough) and the score-gauge breakdown hint so word
// choice stays consistent instead of each surface re-deriving it.
//   - hasHover()    → true when the primary input can hover (mouse/trackpad).
//   - pointerVerb() → 'Click' on hover-capable devices, 'Tap' on touch.
// SSR / no-matchMedia defaults to the desktop verb ("Click").
export const hasHover = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia('(hover: hover)').matches;
};
export const pointerVerb = () => (hasHover() ? 'Click' : 'Tap');

// S117-T7 (Roadmap Phase 1): canonical viewport breakpoint scale. Semantic
// names (mobile/tablet/desktop/wide/ultra) preferred for new code so media
// queries read intent ("mobile-only sticky bar") instead of implementation
// ("max-width 480"). The legacy sm/md/lg/xl aliases stay for any caller
// that wants the Tailwind-style abbreviation.
//
// Convention:
//   - JS: `if (window.innerWidth < breakpoint.tablet) { … }`
//   - JS responsive hook (preferred): use a `useViewport()` helper if you
//     find yourself re-writing matchMedia logic in 3+ places.
//   - CSS: keep mirrored constants in src/index.css if needed; if you
//     change a value here, sync the CSS var.
//
// Token       Px    Use
// mobile      375   iPhone SE / portrait phones
// tablet      768   iPad portrait / large phones landscape
// desktop     1024  laptop / iPad landscape
// wide        1440  16" laptop / standard external monitor
// ultra       1920  Full-HD desktop / 4K-scaled
//
// Legacy aliases (sm/md/lg/xl) approximate the new scale and stay
// exported for any pre-S117 caller that imported them by old name.
export const breakpoint = {
  mobile: 375,
  tablet: 768,
  desktop: 1024,
  wide: 1440,
  ultra: 1920,
  // Legacy aliases — new code should prefer semantic names above.
  sm: 480,
  md: 768,
  lg: 1024,
  xl: 1280,
};

// S117-T7 (Roadmap Phase 1): canonical z-index stacking scale. Use these
// tokens instead of hardcoded `zIndex: 1000` literals so layered surfaces
// (Modal, Tooltip, Popover, Toast) maintain a consistent stacking order.
//
// Convention:
//   - Inline-style: `zIndex: zIndex.modal`
//   - The numbers leave gaps (10) so a one-off "just above modal" surface
//     can use `zIndex.modal + 1` without colliding with the next tier.
//
// Token      Value  Use
// base       0      default flow
// dropdown   10     menus, autocomplete, select-popups
// sticky     20     sticky headers, sticky tabs, sticky CTAs
// overlay    40     scrim / backdrop behind modals + drawers
// modal      50     dialog / modal panel itself
// popover    60     tooltips-as-popovers, contextual menus
// toast      70     toast notifications (above everything navigational)
// tooltip    80     ephemeral hover-tooltips (always topmost)
export const zIndex = {
  base: 0,
  dropdown: 10,
  sticky: 20,
  overlay: 40,
  modal: 50,
  popover: 60,
  toast: 70,
  tooltip: 80,
};

// F025 (P2.5c): single scrim token for overlay backdrops so surfaces stop
// hand-rolling three different recipes (none / 0.5 / 0.7).
// F117 (P6): theme-aware. A 0.7 black over a light UI is a blackout, not a
// scrim — Stripe/Linear light scrims run ~0.2-0.4. Dark keeps the deeper 0.7
// (it sits over the near-black canvas). `scrim` stays the dark default for
// theme-agnostic callers (e.g. the always-dark tour); overlays that know their
// theme call scrimFor(theme).
export const scrim = alpha('#000', 0.7);
export const scrimLight = alpha('#0f172a', 0.35);
export const scrimFor = (theme) => (theme === 'light' ? scrimLight : scrim);
